
import React, { useState, useEffect, useCallback } from 'react';
import { Exercise } from '@/models/LearnTypes';
import FlashCardExerciseBase from './FlashCardExerciseBase';
import { usePerfectLevel } from '@/contexts/PerfectLevelContext';

// Define all possible flash card modes
export type FlashCardMode = 'standard' | 'reverse';

/**
 * Props for the FlashCardExercise component
 */
interface FlashCardExerciseProps {
  currentExercise: Exercise;
  isAnimating: boolean;
  onNext: () => void;
  onPrev?: () => void;
  isLastExercise: boolean;
  nextCard: Exercise | null;
  initialAnimation?: boolean;
  totalExercises?: number;
  exerciseNumber?: number;
  onClose?: () => void;
  exercises?: Exercise[];
  onComplete?: (wordsToReview: string[]) => void;
  onUpdateProgress?: (progress: number) => void;
  // The mode determines which content appears on front/back of card
  mode?: FlashCardMode;
  onLogActivity?: (activityType: string, exercise: Exercise) => void;
  // Perfect level tracking props
  isPerfect?: boolean;
  onImperfect?: () => void;
  // Recycled word indicator
  isRecycledWord?: boolean;
  // Direct word recycling function
  onAddWordToReview?: (wordId: string) => void;
}

/**
 * FlashCard exercise component that can be used in standard or reverse mode
 * Standard: Foreign word on front, translation on back
 * Reverse: Translation on front, foreign word on back
 */
const FlashCardExercise: React.FC<FlashCardExerciseProps> = ({
  currentExercise,
  isAnimating,
  onNext,
  onPrev,
  isLastExercise,
  nextCard,
  initialAnimation,
  totalExercises,
  exerciseNumber,
  onClose,
  exercises = [],
  onComplete,
  onUpdateProgress,
  mode = 'standard', // Default to standard mode
  onLogActivity,
  // Perfect level tracking with default value
  isPerfect = true,
  onImperfect,
  isRecycledWord = false,
  // Direct word tracking function
  onAddWordToReview
}) => {
  // Track failed cards for analytics
  const [failedCards, setFailedCards] = useState<Exercise[]>([]);
  // Track the number of consecutive times a specific card has been failed
  const [cardFailureCount, setCardFailureCount] = useState<Record<string, number>>({});
  // Track if this level had any mistakes for perfect level tracking
  const [hadMistake, setHadMistake] = useState(false);
  
  // Get access to the perfect level context
  const { markLevelImperfect, getLevelPerfectStatus } = usePerfectLevel();
  
  // Function to ensure a level is marked as imperfect
  const ensureImperfectStatus = useCallback((levelIndex: number) => {
    // Only mark level imperfect if it's not already marked
    if (getLevelPerfectStatus(levelIndex)) {
      console.log(`❌ [FlashCardExercise] Marking level ${levelIndex} as imperfect using context`);
      markLevelImperfect(levelIndex);
      
      // Set hadMistake for component-local tracking
      setHadMistake(true);
      
      // For backward compatibility with legacy code
      (window as any).__PERFECT_LEVEL_STATUS__ = false;
      localStorage.setItem('perfect_level_status', 'false');
    }
  }, [getLevelPerfectStatus, markLevelImperfect]);
  
  // Track failed cards for analytics
  useEffect(() => {
    // If we have any failed cards, ensure this level is marked as imperfect
    if (failedCards.length > 0 && !hadMistake) {
      // Get the current level index from URL
      const levelIndex = window.location.pathname.includes('/learn/') ?
        Number(window.location.pathname.split('/').pop()) : -1;
        
      if (levelIndex >= 0) {
        console.log(`❌ [FlashCardExercise] Cards failed: ${failedCards.length} - marking level ${levelIndex} as imperfect`);
        setHadMistake(true);
        markLevelImperfect(levelIndex);
      }
    }
    
    // Save to localStorage for persistence across components
    try {
      localStorage.setItem('failedCards', JSON.stringify(failedCards.map(card => card.id)));
    } catch (e) {
      console.error('Error saving failed cards to localStorage', e);
    }
  }, [failedCards]);
  
  // Handle card failure ("Practice more" button)
  const handleCardFailed = useCallback((exercise: Exercise) => {
    console.log('Card failed handler called with exercise:', exercise);
    
    // We track failed words for analytics only
    
    // Add word to review list for analytics if the callback is provided
    if (onAddWordToReview) {
      console.log(`📊 [handleCardFailed] Adding ${exercise.id} to wordsToReview for analytics`);
      onAddWordToReview(exercise.id);
    }
    
    // Store the failed card IDs in localStorage for persistence
    try {
      // Get existing failed card IDs
      const existingIds = JSON.parse(localStorage.getItem('current_level_failed_cards') || '[]');
      
      // Add this card ID if not already present
      if (!existingIds.includes(exercise.id)) {
        existingIds.push(exercise.id);
        localStorage.setItem('current_level_failed_cards', JSON.stringify(existingIds));
        console.debug(`⚠️ Added failed card ${exercise.id} to localStorage`);
      }
    } catch (e) {
      console.error('Error updating failed cards in localStorage', e);
    }
    
    // Mark the level as imperfect when a card fails
    if (onImperfect) {
      console.debug('[FlashCardExercise] Marking level as imperfect due to card failure');
      onImperfect();
    }
    
    // Track consecutive failures for this specific card
    setCardFailureCount(prev => {
      const currentCount = prev[exercise.id] || 0;
      const newCount = currentCount + 1;
      
      console.log(`Card ${exercise.id} failure count: ${newCount}`);
      
      // Add the failed card to our tracking list
      setFailedCards(prevFailedCards => {
        // Check if the card is already in the failed cards list
        const isDuplicate = prevFailedCards.some(card => card.id === exercise.id);
        
        // Only add if it's not already in the list
        if (!isDuplicate) {
          console.log(`Adding card ${exercise.id} to failed cards list`);
          return [...prevFailedCards, exercise];
        } else {
          console.log(`Card ${exercise.id} already in failed cards list - not adding again`);
          return prevFailedCards;
        }
      });
      
      return { ...prev, [exercise.id]: newCount };
    });
  }, [onImperfect, onAddWordToReview]);
  
  // Reset the failure count when a card is answered correctly
  const resetCardFailureCount = useCallback((exerciseId: string) => {
    setCardFailureCount(prev => {
      if (prev[exerciseId]) {
        const newCounts = { ...prev };
        delete newCounts[exerciseId];
        console.log(`Reset failure count for card ${exerciseId}`);
        return newCounts;
      }
      return prev;
    });
  }, []);
  
  // Handle successful or failed completion of a card
  const handleExerciseComplete = useCallback((wasSuccess: boolean, wasFailed?: boolean, skipProgressUpdate?: boolean) => {
    // If this card is failed, mark the level as imperfect
    if (wasFailed) {
      console.log(`❌ [FlashCardExercise] Card failed: ${currentExercise.word} - marking level as imperfect`);
      setHadMistake(true);
      
      // Get the level index from URL
      const levelIndex = window.location.pathname.includes('/learn/') ?
        Number(window.location.pathname.split('/').pop()) : -1;
        
      // Use our context to ensure this level is marked as imperfect
      if (levelIndex >= 0) {
        ensureImperfectStatus(levelIndex);
      }
      
      // Call parent handler if available
      if (onImperfect) {
        console.log('[FlashCardExercise] Calling parent onImperfect handler');
        onImperfect();
      }
    }
    
    // Determine if this is the last card to handle before completing the level
    const isLastCard = isLastExercise;
    
    // Process card state changes based on success/failure
    if (wasFailed && currentExercise) {
      // Card was failed - add to failed cards if not already tracked
      setFailedCards(prev => {
        if (!prev.some(card => card.id === currentExercise.id)) {
          const updatedCards = [...prev, currentExercise];
          
          // Handle last card completion separately after state update
          if (isLastCard) {
            // If this is the last card, pass failed card IDs for tracking
            const failedCardIds = updatedCards.map(card => card.id);
            console.log('Last card failed - will track cards:', failedCardIds);
            if (onComplete) {
              setTimeout(() => onComplete(failedCardIds), 0);
            }
          }
          
          return updatedCards;
        }

        // Handle last card completion when card was already in failed list
        if (isLastCard && onComplete) {
          const failedCardIds = prev.map(card => card.id);
          console.log('Last card already in failed - will track cards:', failedCardIds);
          setTimeout(() => onComplete(failedCardIds), 0);
        }
        
        return prev;
      });

      // If not the last card, move to next
      if (!isLastCard) {
        // If skipProgressUpdate is true, we don't want to increment the progress bar
        if (skipProgressUpdate && onUpdateProgress && exerciseNumber && totalExercises) {
          console.log('Skipping progress update for failed card');
          // Maintain the current progress by explicitly calling onUpdateProgress with the current progress
          onUpdateProgress(exerciseNumber / totalExercises);
        }
        onNext();
      }
    } else if (wasSuccess && currentExercise) {
      // Card was successful - check if it was previously failed
      
      // Reset the failure count for this card
      resetCardFailureCount(currentExercise.id);
      
      setFailedCards(prev => {
        // Check if current card was in failed cards list
        const cardIndex = prev.findIndex(card => card.id === currentExercise.id);
        let updatedCards = prev;
        
        if (cardIndex >= 0) {
          console.log('Removing card from failed cards after correct answer:', currentExercise.id);
          // Remove the card from the failed cards array
          updatedCards = [...prev];
          updatedCards.splice(cardIndex, 1);
        }
        
        // Handle last card completion separately after state update
        if (isLastCard) {
          const remainingFailedCards = updatedCards.length;
          
          console.log(`Successful card - Remaining failed cards: ${remainingFailedCards}`);
          
          if (remainingFailedCards === 0) {
            // All cards were successful - complete with empty array
            console.log('All cards successful - completing level with no failed cards');
            
            if (onComplete) {
              setTimeout(() => onComplete([]), 0);
            }
          } else {
            // Handle remaining failed cards - track them
            const failedCardIds = updatedCards.map(card => card.id);
            console.log('Completed with remaining failed cards:', failedCardIds);
            
            // Set the global flag for failed cards
            (window as any).__HAD_RECYCLED_WORDS__ = true;
            
            // Get the level index from URL
            const levelIndex = window.location.pathname.includes('/learn/') ?
              Number(window.location.pathname.split('/').pop()) : -1;
              
            // Use our context to ensure this level is marked as imperfect
            if (levelIndex >= 0) {
              ensureImperfectStatus(levelIndex);
            }
            
            // Add failed card IDs to localStorage for persistence
            try {
              localStorage.setItem('current_level_failed_cards', JSON.stringify(failedCardIds));
            } catch (e) {
              console.error('Error saving failed cards to localStorage', e);
            }
            
            // If there are failed cards, level is not perfect
            if (isPerfect && onImperfect) {
              console.debug('[FlashCardExercise] Marking level as imperfect due to remaining failed cards');
              onImperfect();
            }
            
            if (onComplete) {
              setTimeout(() => {
                console.debug(`Passing ${failedCardIds.length} failed card IDs to onComplete`);
                onComplete(failedCardIds);
              }, 0);
            }
          }
        }
        
        return updatedCards;
      });
      
      // If not the last card, move to next
      if (!isLastCard) {
        // If skipProgressUpdate is true, we don't want to increment the progress bar
        if (skipProgressUpdate && onUpdateProgress && exerciseNumber && totalExercises) {
          console.log('Skipping progress update for failed card');
          // Maintain the current progress by explicitly calling onUpdateProgress with the current progress
          onUpdateProgress(exerciseNumber / totalExercises);
        }
        onNext();
      }
    } else {
      // Normal flow - handle based on card position
      if (isLastCard) {
        // If it's the last card, check if there are any failed cards
        const remainingFailedCards = failedCards.length;
        
        if (remainingFailedCards === 0) {
          console.log('Last card with no failed cards - completing level');
          if (onComplete) {
            onComplete([]);
          } else {
            onNext();
          }
        } else {
          // We have failed cards to track
          const failedCardIds = failedCards.map(card => card.id);
          console.log('Last card with failed cards to track:', failedCardIds);
          
          // Add failed card IDs to localStorage for persistence
          try {
            localStorage.setItem('current_level_failed_cards', JSON.stringify(failedCardIds));
          } catch (e) {
            console.error('Error saving failed cards to localStorage', e);
          }
          
          // If there are failed cards, level is not perfect
          if (isPerfect && onImperfect) {
            console.debug('[FlashCardExercise] Marking level as imperfect due to remaining failed cards');
            onImperfect();
          }
          
          if (onComplete) {
            console.debug(`Passing ${failedCardIds.length} failed card IDs to onComplete`);
            onComplete(failedCardIds);
          } else {
            onNext();
          }
        }
      } else {
        // Not the last card, just move to next
        // If skipProgressUpdate is true, we don't want to increment the progress bar
        if (skipProgressUpdate && onUpdateProgress && exerciseNumber && totalExercises) {
          console.log('Skipping progress update for normal flow');
          // Maintain the current progress by explicitly calling onUpdateProgress with the current progress
          onUpdateProgress(exerciseNumber / totalExercises);
        }
        onNext();
      }
    }
  }, [
    isLastExercise, 
    onComplete, 
    onNext, 
    currentExercise, 
    failedCards, 
    onImperfect, 
    isPerfect, 
    resetCardFailureCount, 
    onUpdateProgress, 
    exerciseNumber, 
    totalExercises,
    hadMistake,
    ensureImperfectStatus
  ]);
  
  // Determine front/back content based on the selected mode
  const frontContent = mode === 'standard' 
    ? currentExercise.word 
    : currentExercise.translation;
  
  const backContent = mode === 'standard' 
    ? currentExercise.translation 
    : currentExercise.word;
  
  // Function to consistently update perfect level flags
  const updatePerfectLevelFlags = (hasMistake: boolean) => {
    // Update global window variables
    (window as any).__FLASHCARD_EXERCISE_HAD_MISTAKE__ = hasMistake;
    (window as any).__FLASHCARD_EXERCISE_IS_PERFECT__ = !hasMistake;
    (window as any).__PERFECT_LEVEL_STATUS__ = !hasMistake;
    
    // Update localStorage flags
    try {
      localStorage.setItem('flashcard_exercise_had_mistake', String(hasMistake));
      localStorage.setItem('flashcard_exercise_is_perfect', String(!hasMistake));
      localStorage.setItem('perfect_level_status', String(!hasMistake));
      
      // Set level-specific flags
      const pathname = window.location.pathname;
      const match = pathname.match(/\/learn\/[^\/]+\/([0-9]+)/);
      if (match && match[1]) {
        const levelIndex = parseInt(match[1], 10);
        localStorage.setItem(`flashcard_exercise_had_mistake_level_${levelIndex}`, String(hasMistake));
        localStorage.setItem(`level_${levelIndex}_perfect_status`, String(!hasMistake));
      }
    } catch (e) {
      console.error('Error updating perfect level flags:', e);
    }
  };
  
  // Initialize flags at component mount
  useEffect(() => {
    // Reset flags when component mounts
    const pathname = window.location.pathname;
    const match = pathname.match(/\/learn\/[^\/]+\/([0-9]+)/);
    
    try {
      // Reset global flags
      (window as any).__FLASHCARD_EXERCISE_HAD_MISTAKE__ = false;
      (window as any).__FLASHCARD_EXERCISE_IS_PERFECT__ = true;
      
      // Reset localStorage flags
      localStorage.setItem('flashcard_exercise_had_mistake', 'false');
      localStorage.setItem('flashcard_exercise_is_perfect', 'true');
      
      // Reset level-specific flags if we can determine the level
      if (match && match[1]) {
        const levelIndex = parseInt(match[1], 10);
        localStorage.setItem(`flashcard_exercise_had_mistake_level_${levelIndex}`, 'false');
      }
    } catch (e) {
      console.error('Error resetting perfect level flags:', e);
    }
  }, []);
  
  // Effect to sync state when component unmounts
  useEffect(() => {
    return () => {
      // Final update of flags when component unmounts
      updatePerfectLevelFlags(hadMistake);
    };
  }, [hadMistake]);
  
  return (
    <FlashCardExerciseBase
      currentExercise={currentExercise}
      isAnimating={isAnimating}
      onNext={handleExerciseComplete}
      onPrev={onPrev}
      isLastExercise={isLastExercise}
      nextCard={nextCard}
      initialAnimation={initialAnimation}
      totalExercises={totalExercises}
      exerciseNumber={exerciseNumber}
      frontContent={frontContent}
      backContent={backContent}
      onClose={onClose}
      onCardFailed={handleCardFailed}
      onLogActivity={onLogActivity}
      isPerfect={isPerfect}
      onImperfect={onImperfect}
    />
  );
};

export default FlashCardExercise;
